import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import { TrustAPI } from '@/shared/modules/trust-api';
import { Options, Vue } from 'vue-class-component';
import { Inject, Prop } from 'vue-property-decorator';
import { TimeEntryView, TrustTeamMemberView } from '@/shared/modules/typescript-trustapi-client/models';
import store from '@/trust-admin-app/store';
import { AppInsights } from '@/trust-admin-app/appInsights';
import { EventEmitter } from 'events';
import CalendarView from '@/shared/components/calendar-view/index.vue';
import { EventInput, ViewApi } from '@fullcalendar/core';
import moment from "moment";
import { orderBy } from 'lodash';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import { eventTransform } from '@/shared/components/helpers';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import DashboardStats from '@/shared/components/dashboard-stats/index.vue';
import { RoleType } from '@/trust-admin-app/models/user';
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    CalendarView,
    DashboardStats
  },
  mixins: [FormatDateTimeMixin]
})
export default class InvestigatorDashboard extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Prop() 
  private investigatorId!: number;

  private currentCalendarView: ViewApi = null as any;

  private showEditModal = false;
  private validatingEntrance = false;

  private loadingInvestigatorDetails = true;
  private errorLoadingInvestigatorDetails = true;

  private loadingTimeEntries = true;
  private errorLoadingTimeEntries = true;

  private loadingEvents = false;

  private investigatorDetails: TrustTeamMemberView = {}

  private timesheetEntries: TimeEntryView[] = [];

  private eventData: EventInput[] = [];

  private leaveScheduled = 0;

  mounted(): void {
    window.scrollTo(0, 0);
    this.loadData();
    this.eventCtrl.on('reload-investigator-dashboard', this.loadData);
  }

  private beforeUnmounted(): void {
    this.eventCtrl.removeListener('reload-investigator-dashboard', this.loadData);
  }

  private loadData(): void {
    this.getInvestigatorDetails();
    this.getTimeEntries();
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private getInvestigatorDetails(): void {
    this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMemberByIdMemberIdGet(this.selectedTrustTeamMember.id)
      .then(res => {
        // console.log(res.data.data);
        this.loadingInvestigatorDetails = false;
        this.errorLoadingInvestigatorDetails = false;
        this.investigatorDetails = res.data.data;
        store.dispatch('setSelectedTrustTeamMember',  this.investigatorDetails);
      })
      .catch(error => {
        this.loadingInvestigatorDetails = false;
        this.errorLoadingInvestigatorDetails = true
        this.eventCtrl.emit('show-snackbar', 'error', error.message);
        this.ai.trackException('Error getting investigator: ' + error);
      });
  }

  private getTimeEntries(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryGetTimeEntriesByTrustTeamMemberIdPost({
      trustTeamMemberId: this.selectedTrustTeamMember.id,
      startDate: new Date('2022-01-01'),
      endDate: new Date('2099-01-01'),
      showMissingTime: false
    })
    .then(res => {
      this.loadingTimeEntries = false;
      this.errorLoadingTimeEntries = false;
      this.leaveScheduled = res.data.data.length;
      this.timesheetEntries = res.data.data.filter((d: { id: number; }) => {
        return d.id !== 0;
      }).slice(-5);
      this.timesheetEntries =  orderBy(
        this.timesheetEntries,
        'startDateTime',
        'desc'
      );
    })
    .catch(error => {
      this.loadingTimeEntries = false
      this.errorLoadingTimeEntries = true
      this.eventCtrl.emit('show-snackbar', 'error', error);
      this.ai.trackException('Error getting investigator timesheet entries: ' + error);
    });
  }

  private async getEvents(view: ViewApi): Promise<void> {
    try {
      this.loadingEvents = true;
      const resp: any = await this.apiService.CalendarApi?.apiV2CalendarGetEventsByTrustTeamMemberIdPost({
        trustTeamMemberId: this.selectedTrustTeamMember.id,
        startDate: new Date(view.activeStart),
        endDate: new Date(view.activeEnd),
        showMissingTime: true
      });
      this.eventData = [...eventTransform(resp.data.data)];
    } catch (error) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to get time events');
      this.ai.trackException('Error getting investigations: ' + error);
    } finally {
      this.loadingEvents = false;
    }
  }

  private uiDateTimeRangeChanged(view: ViewApi): void {
    this.currentCalendarView = view;
    this.getEvents(view);
  }

  private openAddEditMemberModal(): void {
    this.eventCtrl.emit('showAddEditMemberModal', this.selectedTrustTeamMember);
  }

  private goToCalendar(): void {
    this.$router.push({ name: RouteN.TA.InvestigatorCalendar.n, query: {memberId: this.investigatorDetails.id} });
  }

  private goToTimesheets(): void {
    this.$router.push({ name: RouteN.TA.InvestigatorTimesheets.n, query: {memberId: this.investigatorDetails.id} });
  }

  private goToInvestigations(): void {
    this.$router.push({ name: RouteN.TA.InvestigatorInvestigations.n, query: {memberId: this.investigatorDetails.id} });
  }

  private getDuration(start: Date, end: Date): string {
    return moment(start).format('DD/MM/YYYY HH:mm') + ' - ' + moment(end).format('DD/MM/YYYY HH:mm')
  }

  get usrAcct(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get showAddUserButton(): boolean {
    return this.$accessPermitted(this.usrAcct.user.roles, [
      RoleType.TrustAdmin,
      RoleType.TrustManager,
      RoleType.TrustSuperAdmin
    ]);
  }
  get selectedTrustTeamMember(): TrustTeamMemberView {
    return store.getters.getSelectedTrustTeamMember;
  }

}